<template>
  <div class="detail-column">
    <template v-for="(value, key) in columnInfo"
              :key="key">
      <div v-if="componentAvailable(value)"
           :id="value"
           class="card detail-card column-item"
           :class="{
              'where-used-card': value === 'WhereUsed',
              'part-collections-outer': value === 'PartCollections' && hasPartCollections,
              'expanded-part-collections-outer': value === 'PartCollections' && hasAnExpandedCollection,
              'part-collections-outer--empty': value === 'PartCollections' && !hasPartCollections
            }">
        <div v-if="titleMap.has(value)"
             class="card-header-title">
          {{ $t(titleMap.get(value)) }}
        </div>
        <component  v-if="componentMap.has(value)"
                    :is="componentMap.get(value)"
                    :id="castIdToNumber(content.id)"
                    :content="content"
                    :type="content.type"
                    :showTags="false"
                    :height="componentHeight(value)"/>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount } from "vue"
import { useStore } from "vuex"

import useDetailPage from "./useDetailPage"
import useRouteParams from "@/composables/useRouteParams"
import { useBreakpoints } from "@/helpers"
import { usePartCollectionsData } from "@/composables/collections/usePartCollectionsData"
import i18n from "@/locales"

import InfoWrapper from "@/components/library/content/info/InfoWrapper.vue"
import InfoTags from "@/components/library/content/info/tags/InfoTags.vue"
import WhereUsed from "@/components/library/content/whereused/WhereUsed.vue"
import Related from "@/components/library/content/related/Related.vue"
import Comments from "@/components/library/content/comments/Comments.vue"
import Suggestions from "@/components/library/content/suggestions/Suggestions.vue"
import Attachments from "@/components/library/content/attachments/Attachments.vue"
import PartCollectionsWrapper from "../collections/PartCollectionsWrapper.vue"

defineProps({
  columnInfo: {
    type: Object,
    required: true
  }
})

const store = useStore()
const { width } = useBreakpoints()
const { id } = useRouteParams()
const {
  content,
  showComments,
  showSuggestions,
  showInfoTags,
  showWhereUsed,
  showRelated,
  showAttachments,
  canViewAttachments,
  showPartCollections,
  hasPartCollections
} = useDetailPage()
const { hasAnExpandedCollection } = usePartCollectionsData(
  id.value,
  (part: any) => part
)

const isWidget = computed(() => store.getters['widgets/isWidget'])

const isMobileView = computed(() => width.value <= 768)

function castIdToNumber (id: string) {
  return parseInt(id)
}

function componentAvailable (componentName: String): boolean {
  switch (componentName) {
    case 'InfoWrapper':
      return true
    case 'InfoTags':
      return showInfoTags.value
    case 'WhereUsed':
      return showWhereUsed.value
    case 'Related':
      return showRelated.value
    case 'Attachments':
      return showAttachments.value
    case 'Comments':
      return showComments.value
    case 'Suggestions':
      return showSuggestions.value
    case 'PartCollections':
      return showPartCollections.value
    default:
      return false
  }
}

function componentHeight (componentName: String): string {
  if (componentName === 'WhereUsed' && !isMobileView.value) { return '90vh' }
  else { return '60vh'}
}

const componentMap = new Map<string, any>([
  ['InfoWrapper', InfoWrapper],
  ['InfoTags', InfoTags],
  ['WhereUsed', WhereUsed],
  ['Related', Related],
  ['Attachments', Attachments],
  ['Comments', Comments],
  ['Suggestions', Suggestions],
  ['PartCollections', PartCollectionsWrapper]
])

const titleMap = new Map<string, any>([
  ['InfoTags', i18n.global.tc('detail', 2)],
  ['WhereUsed', i18n.global.t('whereUsed')],
  ['Related', i18n.global.t('related')],
  ['Attachments', i18n.global.tc('attachment', 2)],
  ['Comments', i18n.global.tc('comment', 2)],
  ['Suggestions', i18n.global.tc('suggestion', 2)],
  ['PartCollections', i18n.global.tc('partCollection', 2)]
])

onBeforeMount(async () => {
  if (canViewAttachments.value || isWidget.value) {
    await store.dispatch("content/getAttachments")
  }
})
</script>

<style scoped lang="scss">
.card {
  padding: .5em;
  margin: 1em;
  max-height: 60vh;
  min-height: 20vh;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;

  .card-header-title {
    flex: 0 0 auto;
  }

  &.no-scroll {
    overflow-y: hidden;
  }
}

.where-used-card {
  @media only screen and (min-width: 768px) {
    max-height: 90vh;
  }
}

.part-collections-outer {
  overflow-y: hidden;
  height: max(50vh, 425px);
  min-height: max(50vh, 425px);
  display: flex;
  flex-direction: column;
}

.part-collections-outer--empty {
  height: auto;
  overflow-y: hidden;
}

.expanded-part-collections-outer {
  height: max(66vh, 561px);
  min-height: max(66vh, 561px);
}

.card:first-of-type {
  .column-item:first-of-type {
    margin-top: 0;
  }
}
</style>
